import { Component, ViewEncapsulation, ViewChild, OnInit, OnDestroy, ViewChildren, QueryList, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { fadeInOut } from '../../shared/helpers/animations';
import { AuthorizationService, CompanyContactService, StorageManagerService } from '../../shared/services/index';
import { GridComponent, QueryCellInfoEventArgs } from '@syncfusion/ej2-angular-grids';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { EmitType } from '@syncfusion/ej2-base';
import { DialogComponent, TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { ILLBusinessCategory } from '../../shared/models/business-category.model';
import { Utilities } from '../../shared/services/utilities';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-business-category',
    templateUrl: './business-category.component.html',
    styleUrls: ['./business-category.component.scss'],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class BusinessCategoryComponent implements OnInit, OnDestroy, AfterViewInit {

    constructor (private router: Router, private toastr: ToastrService,
        private fb: UntypedFormBuilder, private authorizationService: AuthorizationService,
        private cdRef: ChangeDetectorRef, private activatedRoute: ActivatedRoute,
        private utilService: Utilities,
        private storageManagerService: StorageManagerService, private companyService: CompanyContactService, private sanitizer: DomSanitizer) {
    }

    @ViewChild('businessCategory_grid') public businessCategory_grid: GridComponent;
    @ViewChild('EditBCModalDialog') EditBCModalDialog: DialogComponent;
    @ViewChildren(TooltipComponent) toolTips: QueryList<TooltipComponent>;
    public tooltipObj: TooltipComponent;
    public frmEditBusinessCategory: UntypedFormGroup;
    businessCategory_grid_data: ILLBusinessCategory[];
    public initialPage: Object;
    public formatOptions: object;
    public toolbarOptions: Object;
    currBusinessCategoryId: string;
    loading = false;
    submitted = false;
    errorMessage: string;
    bHasPermission = false;
    sub: Subscription;
    formData: any;
    EditBCModalDialogVisible = false;
    position: object = { X: 'center', Y: 'center' };
    modalWidth = '30%';
    modalHeight = 'auto';
    animationSettings: Object = { effect: 'Zoom' };
    isModal = true;
    public clipmodecolumn = { class: 'e-tooltipcolumn' };

  ngAfterViewInit(): void {

    this.sub = this.toolTips.changes.subscribe((comps: QueryList<TooltipComponent>) => {
      this.tooltipObj = comps?.first;
    });
  }

    ngOnInit(): void {

        this.formData = this.activatedRoute.snapshot.data['businessCategoryData'];
        if (this.formData && this.formData[0]) {
            this.businessCategory_grid_data = this.formData[0];
        }

        this.frmEditBusinessCategory = this.fb.group({
            name: ['', { validators: [Validators.required], updateOn: 'blur' }],
            description: ['', { updateOn: 'blur' }],
            status: ['Active', { validators: [Validators.required], updateOn: 'blur' }],
        });

        // Setup the tool bar configuration
        this.toolbarOptions = ['Search'];
        this.initialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100, 'All'] };
        this.formatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() + ' hh:mm a' };

        this.utilService.toggleClientDropdownState(true);

        // Check if user has write permission. If no, disable the "Save" and "Add Business Category" button
        this.checkUserPermission();

        // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
        this.initTabIndex();
    }

    initTabIndex() {
      (document.activeElement as HTMLElement).blur();

      const main = document.getElementById("firstDiv");

      if (main) {
        main.focus();
      }
    }

    // Open Modal popup to allow user to create/modify business category
    openEditModal(categoryId: string) {
        // Only display the edit popup window when user has the write permission
        if (this.bHasPermission) {
            // before open the modal, clear out the controls
            this.f.description.setValue('');
            this.f.name.setValue('');
            this.f.status.setValue('Active');
            this.currBusinessCategoryId = '';
            this.submitted = false;

            // Modify exsiting business category
            if (categoryId) {
                const bc = this.businessCategory_grid_data.filter(x => x.categoryId === categoryId);
                if (bc && bc.length > 0) {
                    this.f.description.setValue(bc[0].description);
                    this.f.name.setValue(bc[0].label);
                    this.f.status.setValue(bc[0].activeFlag && bc[0].activeFlag === 'A' ? 'Active' : 'Inactive');
                    this.currBusinessCategoryId = bc[0].categoryId;
                }
            }

            // show modal to allow user to create/modify business category
            this.EditBCModalDialog.show();
        }
    }

    // Create/Update business category
    saveBusinessCategory(): void {
        this.submitted = true;
        this.loading = true;
        this.errorMessage = '';
        const businessCategory = new ILLBusinessCategory();

        // stop here if form is invalid
        if (this.frmEditBusinessCategory.invalid) {
            this.loading = false;
        } else {

            if (this.frmEditBusinessCategory.dirty) {
                this.EditBCModalDialog.hide();
                this.frmEditBusinessCategory.markAsPristine();
                businessCategory.description = this.f.description.value;
                businessCategory.categoryId = this.currBusinessCategoryId ? this.currBusinessCategoryId : null;
                businessCategory.activeFlag = this.f.status.value === 'Active' ? 'A' : 'I';
                businessCategory.label = this.f.name.value;
                const modifiedByUser = this.storageManagerService.read('userData') ? this.storageManagerService.read('userData').name : '';

                this.sub = this.companyService.createBusinessCategory(businessCategory, modifiedByUser).subscribe(data => {
                    this.loading = false;
                    this.submitted = false;
                    this.toastr.success('Business Category saved successfully.');
                    // Update the business category list on the form
                    this.refreshBusinessCategoryGrid();
                    this.cdRef.detectChanges();
                },
                error => {
                    this.loading = false;
                    this.submitted = false;
                    this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
                    this.cdRef.detectChanges();
                });
            } else {
                this.submitted = false;
                this.loading = false;
                this.toastr.warning('No changes to save.');
                this.EditBCModalDialog.hide();
            }
        }
    }

     // Update the business category list on the form
    refreshBusinessCategoryGrid() {
        this.sub = this.companyService.getBusinessCategories(true).subscribe(data => {
            this.businessCategory_grid_data = data;
        },
        error => {
          this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
        });
    }

    // Close the modal popup
    cancelBtnClicked() {
        this.EditBCModalDialog.hide();
    }
    // Check if user has write permission. If no, disable the "Save" button
    checkUserPermission() {
        if (this.authorizationService.hasPermission('SSDP.CanWriteBusinessCategory') || this.authorizationService.hasPermission('SSDP.CanDeleteBusinessCategory')) {
            this.bHasPermission = true;
        }
    }

    // Format "Status" column on grid
    customizeCell(args: QueryCellInfoEventArgs) {
        if (args.column.field === 'activeFlag') {
            if (args.data['activeFlag'] === 'A') {
                args.cell.innerHTML = 'Active';
            } else {
                args.cell.innerHTML = 'Inactive';
            }
        }
    }

    // Close modal popup
    onBuildModalOverlayClick: EmitType<object> = () => {
        this.EditBCModalDialog.hide();
    }

    // Change grid filter operator from "startwith" to "contains" for columns that are string
    public begin(args: any): void {
        if (args.requestType === 'filtering') {
            if (args.columns) {
                args.columns.forEach(col => {
                    switch (args.currentFilteringColumn) {
                        default:
                            col.operator = 'contains';       // set the “contains” operator for the column
                            break;
                    }
                });
            }
        }
    }

    // convenience getter for easy access to form fields
  get f() { return this.frmEditBusinessCategory.controls; }

  tooltipValue(args) {

    let grid;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      grid = this.businessCategory_grid;

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltipObj.cssClass = 'customTooltip';
      this.tooltipObj.content = rowData[(rowInfo.column as any).field];
    }
  }

    ngOnDestroy(): void {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.utilService.toggleClientDropdownState(false);
    }
}
